/**
 * ComingUpBand: "Coming up" as a full-width band between the Directory's
 * filters and its roster table -- not a fixed-width side column, which
 * would force the table narrow enough to fall into its own horizontal
 * scroll on an ordinary laptop screen.
 *
 * One row of compact chips, in date order, at most COMING_UP_BAND_LIMIT
 * visible ('🎂 Lynn · 17 Sep', '🎉 Jill · 5 years · 1 Oct' --
 * occasionChipText); the row wraps onto a second line rather than
 * scrolling. Past the limit, a "See all N" chip opens a popover with the
 * complete grouped list (This week / Next week / Later this month / Next
 * month), reusing ComingUpPanel's grouping uncapped. Nothing in the next
 * six weeks reads as one quiet line, no chips, no "See all".
 */

import { useState, type ReactNode } from 'react';
import { X } from 'lucide-react';
import {
  COMING_UP_BAND_LIMIT,
  occasionChipAriaLabel,
  occasionChipText,
  upcomingOccasions,
  type TeamDirectoryPerson,
} from '@/lib/teamDirectory';
import { SECTION_EYEBROW } from '@/components/workspace/drawer/drawerLayout';
import { Button } from '@/components/primitives/Button';
import { Chip } from '@/components/primitives/Chip';
import { useDialogDismiss } from '@/components/primitives/useDialogDismiss';
import { ComingUpPanel } from './ComingUpPanel';

export interface ComingUpBandProps {
  people: TeamDirectoryPerson[];
  today: string;
}

export function ComingUpBand({ people, today }: ComingUpBandProps): ReactNode {
  const [open, setOpen] = useState(false);
  const occasions = upcomingOccasions(people, today);
  const visible = occasions.slice(0, COMING_UP_BAND_LIMIT);
  const dialogRef = useDialogDismiss<HTMLDivElement>(() => setOpen(false));

  return (
    <section data-testid="coming-up" aria-label="Coming up" className="relative">
      {occasions.length === 0 ? (
        <p className="text-sm text-ink-soft" data-testid="coming-up-empty">
          The next six weeks are clear.
        </p>
      ) : (
        <div className="flex flex-wrap items-center gap-2" data-testid="coming-up-chips">
          {visible.map((o) => (
            <Chip
              key={`${o.roster_id}-${o.kind}`}
              variant="neutral"
              data-testid={`coming-up-chip-${o.roster_id}-${o.kind}`}
              aria-label={occasionChipAriaLabel(o)}
            >
              {occasionChipText(o)}
            </Chip>
          ))}
          {occasions.length > visible.length && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setOpen(true)}
              data-testid="coming-up-see-all"
            >
              See all {occasions.length}
            </Button>
          )}
        </div>
      )}

      {open && (
        <div
          ref={dialogRef}
          tabIndex={-1}
          role="region"
          aria-label="All upcoming birthdays and work anniversaries"
          data-testid="coming-up-popover"
          className="absolute left-0 top-full z-20 mt-2 w-[min(26rem,90vw)] max-h-[70vh] overflow-auto rounded-card border border-sage-800/[8%] bg-paper p-4 shadow-sh2 focus-visible:outline-none"
        >
          <div className="mb-2 flex items-center justify-between gap-3">
            <h2 className={SECTION_EYEBROW}>Coming up</h2>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-label="Close"
              onClick={() => setOpen(false)}
              data-testid="coming-up-popover-close"
              icon={<X size={14} aria-hidden="true" />}
            />
          </div>
          <ComingUpPanel people={people} today={today} />
        </div>
      )}
    </section>
  );
}
